/**
 * The Chaos Director's allowlist.
 *
 * READ THIS BEFORE ADDING A VARIANT.
 *
 * There is no string field in `ChaosMutation` other than `kind`. That is not an
 * accident and it is not a style choice — it is the enforcement mechanism for
 * PRD §8 constraint 3: *the action schema is structurally incapable of naming a
 * player as a target*. A director that cannot express "hurt Rodrigo" cannot be
 * prompt-injected into hurting Rodrigo, cannot be bribed into it, and cannot do
 * it by accident. Prompts are advice; types are enforcement, and
 * `_AllPayloadFieldsAreNumbers` below fails the build if that ever stops being
 * true, since player ids are strings and every payload field is a number.
 *
 * Concretely, that bans:
 *   - any `playerId` / `targetId` / `sessionId` field,
 *   - any free-form string (a `note` field is a smuggling channel),
 *   - any array of ids, any nested record keyed by id.
 *
 * If you need a new mutation and it seems to require a player reference, it does
 * not belong in a wagered match. Express it as geometry (a position, a radius, a
 * sector index) and let the documented spatial rule pick who it lands on.
 *
 * `validateChaosMutation` enforces the same thing at runtime with an EXACT key
 * check, because an LLM will eventually be handing us this as JSON and "extra
 * keys are ignored" is how `targetPlayerId` gets ignored all the way into the
 * sim.
 *
 * THIS UNION MIRRORS `Mutation` IN `@chaos/sim`, field for field. The two are
 * declared separately because the backend talks to the simulation through the
 * `SimEngine` interface and only `match/simEngine.chaos.ts` may name the sim
 * package; `agent/mutations.contract.test.ts` pins them together so the mirror
 * cannot drift. A mutation the sim cannot apply is a mutation the director must
 * not be able to schedule.
 */
// ---------------------------------------------------------------------------
// Runtime validation
// ---------------------------------------------------------------------------
/**
 * Field name -> [min, max] for every numeric field, per applicable kind. Exact
 * key sets, so a payload carrying anything we did not name is rejected outright
 * rather than partially applied.
 *
 * Mirrors `MUTATION_BOUNDS` in @chaos/sim; the contract test asserts they are
 * identical, because two allowlists that disagree are one allowlist and one
 * hole.
 */
export const MUTATION_NUMERIC_BOUNDS = {
    low_gravity: { gravityPct: [25, 100], durationMs: [1_000, 120_000] },
    ice_floor: { durationMs: [1_000, 120_000] },
    conveyor_floor: { directionDeg: [0, 360], driftSpeed: [0.25, 1.9], durationMs: [1_000, 120_000] },
    hft_belt_flow: { routeGroup: [0, 2], directionMultiplier: [-1, 1], strengthPct: [100, 200], durationMs: [5_000, 20_000] },
    speed_surge: { speedPct: [100, 160], durationMs: [1_000, 120_000] },
    lava_floor: { dps: [1, 30], durationMs: [1_000, 120_000] },
    cooldown_haste: { hastePct: [1, 60] },
    // Short by design — see the sim's copy of this bound, which the contract test
    // asserts this matches.
    inverted_controls: { durationMs: [1_000, 30_000] },
    player_scale: { scalePct: [70, 130], durationMs: [1_000, 60_000] },
    fog_roll: { densityPct: [0, 100], durationMs: [1_000, 120_000] },
    color_shift: { hueDeg: [0, 360], durationMs: [1_000, 120_000] },
    tremor: { magnitudePct: [0, 100], durationMs: [1_000, 120_000] },
};
/** Why a known kind may never be applied. Reported by the validator verbatim. */
export const EXCLUDED_MUTATION_REASONS = {
    damage_amp: "doubled damage makes Monolith's 55 ultimate a 110 one-shot on 80-HP Volt, " +
        "which breaks the stated TTK pillar. Cut by name in PRD §8 rule 10 — do not re-add it",
    wind_shear: "an arena-wide crosswind applied outside the movement integrator is cancelled " +
        "by the air-deceleration curve and moves nobody. Needs a force slot in MovementEnv first",
    health_surge: "needs a pickup entity, which neither the simulation nor the wire protocol has",
    boss_spawn: "needs an NPC actor in the simulation and on the wire",
    sector_collapse: "needs per-wedge geometry on the wire; a lethal region the client cannot draw " +
        "is not a disclosed ruleset",
};
export const APPLICABLE_MUTATION_KINDS = Object.keys(MUTATION_NUMERIC_BOUNDS).sort();
export const MUTATION_KINDS = [
    ...APPLICABLE_MUTATION_KINDS,
    ...Object.keys(EXCLUDED_MUTATION_REASONS).sort(),
].sort();
export function isMutationKind(v) {
    return MUTATION_KINDS.includes(v);
}
export function isApplicableMutationKind(v) {
    return Object.prototype.hasOwnProperty.call(MUTATION_NUMERIC_BOUNDS, v);
}
export function isChaosMutation(value) {
    return validateChaosMutation(value).ok;
}
export function validateChaosMutation(value) {
    if (typeof value !== "object" || value === null || Array.isArray(value)) {
        return { ok: false, reason: "not an object" };
    }
    const obj = value;
    const kind = obj["kind"];
    if (typeof kind !== "string") {
        return { ok: false, reason: `unknown kind ${JSON.stringify(kind)}` };
    }
    if (isMutationKind(kind) && !isApplicableMutationKind(kind)) {
        const reason = EXCLUDED_MUTATION_REASONS[kind];
        return { ok: false, reason: `${kind} is excluded from every lobby: ${reason}` };
    }
    if (!isApplicableMutationKind(kind)) {
        return { ok: false, reason: `unknown kind ${JSON.stringify(kind)}` };
    }
    const numeric = MUTATION_NUMERIC_BOUNDS[kind];
    const allowed = new Set(["kind", ...Object.keys(numeric)]);
    for (const key of Object.keys(obj)) {
        if (!allowed.has(key)) {
            // The important branch. An ignored extra key is how a player reference
            // reaches the sim in a schema everyone believed was safe.
            return { ok: false, reason: `unexpected field ${JSON.stringify(key)} on ${kind}` };
        }
    }
    for (const [field, bounds] of Object.entries(numeric)) {
        const raw = obj[field];
        if (typeof raw !== "number" || !Number.isFinite(raw)) {
            return { ok: false, reason: `${kind}.${field} must be a finite number` };
        }
        const [lo, hi] = bounds;
        if (raw < lo || raw > hi) {
            return { ok: false, reason: `${kind}.${field}=${raw} outside [${lo}, ${hi}]` };
        }
    }
    if (kind === "hft_belt_flow") {
        if (![0, 1, 2].includes(obj["routeGroup"]))
            return { ok: false, reason: "hft_belt_flow.routeGroup must be 0, 1, or 2" };
        if (![-1, 1].includes(obj["directionMultiplier"]))
            return { ok: false, reason: "hft_belt_flow.directionMultiplier must be -1 or 1" };
    }
    return { ok: true };
}
export function assertChaosMutation(value) {
    const result = validateChaosMutation(value);
    if (!result.ok) {
        throw new ChaosMutationError(`Rejected chaos mutation: ${result.reason}`);
    }
}
export class ChaosMutationError extends Error {
    constructor(message) {
        super(message);
        this.name = "ChaosMutationError";
    }
}
//# sourceMappingURL=mutations.js.map